import {Nav} from 'Shared/frontend/icons/Nav';
import {NavMore} from 'Shared/frontend/nav/NavMore';
import {createSignal} from 'solid-js';
import {Show,For} from 'solid-js'
import { css, cx } from '@emotion/css';
import { fluidType } from 'Shared/common/FluidType';
import { theme, sizes, useSettings } from 'Shared/frontend/SettingsProvider';
import { sanitise } from 'Shared/common/Sanitise';
import { Dynamic } from 'solid-js/web';
import { ShowWrapElement } from 'Shared/frontend/ShowWrapElement';
import { useLocation } from '@solidjs/router';


const overlayStyle = (extraCss?:object) => css(theme().nav.mobile.overlay,{
	position: 'fixed',
	top: 0,
	left: 0,
	right: 0,
	height: '100vh',
	/* Should appear over other overlays: */
	zIndex: 50,
	margin: '0 auto',
	scrollbarWidth: 'none',
	overflow: 'auto',
	animationName: 'slideIn',
	animationDuration: '0.5s',
},extraCss);

const mobileStyle = (extraCss?:object) => css(theme().nav.mobile.backgroundWrap,{
	display: 'flex',
	
	flexWrap: 'nowrap',
	padding: 0,
	position: 'fixed',
	bottom: 0,
	left: 0,
	width: '100%',
	zIndex: 5,

//XXX Shared with StandardDefaultLayout etc
	maxWidth: 1380, /*was 1250 but wider displays 4 carousel items per row */
	marginLeft: 'auto',
	marginRight: 'auto',

	a: {
		flex: '1 1',
	}
},extraCss);

const buttonStyle = () => css(theme().nav.mobile.button, {
//TODO use flex instead of this	
	width: '100%',
});

const activeStyle = () => css(theme().nav.mobile.active);

const buttonContentsStyle = () => css({
	display: 'block',
	margin: '0 auto',
	width: 'calc(30px + 20 * ((100vw - 375px ) / 225 ))',
	maxWidth: '90%',
});

const iconLabelStyle = () => css({
	fontSize: fluidType('px',sizes().endFontShrink,sizes().flipToMobileNav,10,16),
});

const closeIconStyle = () => css({
	...sanitise.button,
	position: 'fixed',
	top: 0,
	right: 0,
	padding: 20,
	color: 'inherit',
	backgroundColor: 'inherit',
	borderRadius: 0,
	fontSize: 40,
	fontWeight: 'bold'
});


export function MobileFooterNav(P)
{
	const underlayStyle = css({height: '7em'});

	const mobile = useSettings().navContent().mobile;

	const maxButtons = () => mobile.includeMoreButton ? mobile.maxButtons - 1 : mobile.maxButtons;

	const [visible,setVisible] = createSignal(false);

	/* Warning: only supports a single overlay. Otherwise cf an 'overlayed' count. cf <Portal> */
	const openOverlay = () => {
		document.body.classList.add('overlayed');
		setVisible(true);

		/*
			Following a link to another page requires this BUT DOESNT WORK - as outside Solid tree.
				onCleanup(() => document.body.classList.remove('overlayed'));
			Relying on the router to cleanup 'overlayed'.
			MutationObserver might be better, but bit verbose.
		*/
	};

	const closeOverlay = () => {
		document.body.classList.remove('overlayed');
		setVisible(false);
	};


	return (
		<ShowWrapElement path={['nav','mobile']}>{ style => <>
			<div class={underlayStyle}>
				<nav class={mobileStyle(style().css)}>
					<For each={mobile.links.slice(0,maxButtons())}>{item =>
						<a href={navItemLink(item)}>
							<button tabindex='-1' class={cx(buttonStyle(),'nav-mobile-button',isActive(item) && 'active',isActive(item) && activeStyle())}>
								<div class={buttonContentsStyle()}>
									<Dynamic component={item.icon} />
								</div>
								<div class={iconLabelStyle()}>{item.label}</div>
							</button>
						</a>
					}</For>

					<Show when={mobile.includeMoreButton}>
						<a href=''>
							<button class={cx(buttonStyle(),'nav-mobile-button')} tabindex='-1' onClick={openOverlay}>
								<div class={buttonContentsStyle()}>
									<Nav />
								</div>
								<div class={iconLabelStyle()}>More</div>
							</button>
						</a>
					</Show>
				</nav>
			</div>

			<Show when={visible()}>
				<ShowWrapElement path={['nav','more']}>{ (moreStyle:() => any) =>
					<div class={overlayStyle(moreStyle().css)}>
						<button class={closeIconStyle()} onClick={closeOverlay}>X</button>

						<NavMore {...P} closeOverlay={closeOverlay} />
					</div>
				}</ShowWrapElement>
			</Show>
		</>}</ShowWrapElement>
	);
}

export function navItemLink(item)
{
	if (item.page)
		return useSettings().pages().find(e => item.page==e.key)?.frontend?.route;
	else if (item.link)
		return useSettings().links()[item.link]?.target;
	else
		/* The 'More' button is a 3rd possibility */
		return '';
};

/*
	Whether this nav item is the page being looked at - what the desktop bar, the mobile footer and
	the 'More' menu all mark with their 'active' class.

	On where the item points rather than on what the page calls itself, and navItemLink() above works
	out where it points for the href anyway - so an item lights up on exactly the page its own link
	goes to.

	The names were never comparable. An item names a page key or a link name; the running page knows
	only its component, and for a custom page that is 'frontend/Custom' plus the venue's key for it.
	So Home, Events, Hire and Contact - links named 'frontend/Home' and friends, custom pages on every
	venue - compared 'frontend/Contact' against 'frontend/Custom/contact' and never lit up. Only Menu
	(a real MenuPage) and the items naming a page key directly ever did.
*/
export function isActive(item)
{
	const target = navItemLink(item);
	if (target == undefined || target == '')
		return false;

	return target == currentPath();
}

/* Solid Router hands back the path as the browser has it, which may carry a trailing slash the
   routes and the links here don't - see the note about it in VenueRoutes.routes(). */
function currentPath(): string
{
	const path = useLocation().pathname;
	return path.length > 1 && path.endsWith('/') ? path.slice(0,-1) : path;
}

